import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon, Skeleton, cn } from "@nexus/ui";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
} from "../../types/preflight.types";
import { formatAssetError } from "../../lib/format-asset-error";
import { creativeDisplayLabel } from "../../lib/format-asset-name";
import {
  RESULT_STATE_META,
  TONE_BG,
  TONE_ORDER,
  TONE_TEXT,
} from "../../constants/result-state";
import {
  criterionRowId,
  featureNote,
  featureResultLabel,
  weightPercent,
} from "../../lib/feature-format";
import {
  mediaKind,
  mediaKindIcon,
  mediaKindLabelKey,
} from "../../lib/media-type";
import { AssetCopyTextButton } from "../AssetCopyTextButton/AssetCopyTextButton";
import { AssetExportMenu } from "../AssetExportMenu/AssetExportMenu";
import { AssetNameLabel } from "../AssetNameLabel/AssetNameLabel";
import { CriteriaBreakdownTable } from "../CriteriaBreakdownTable/CriteriaBreakdownTable";
import { ReportAssetPreview } from "./ReportAssetPreview";
import { ScoreBadge } from "../ScoreBadge/ScoreBadge";
import { StatusBadge } from "../StatusBadge/StatusBadge";

/** Opens the URL a creative was ingested from, since its label no longer spells it out. */
function SourceLink({ url }: { url: string }) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      aria-label={t("preflight.creativeCard.openSource")}
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/40 inline-flex size-11 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-[3px] focus-visible:outline-none [@media(hover:hover)_and_(pointer:fine)]:size-6"
    >
      <Icon name="external-link" size="xs" color="inherit" aria-hidden />
    </a>
  );
}

/** Compact media-type pill (image/video) shown beside the asset name. */
function MediaTypeChip({ contentType }: { contentType: string | null }) {
  const { t } = useTranslation();
  const kind = mediaKind(contentType);
  if (kind === "other") return null;
  return (
    <span className="text-muted-foreground bg-muted/70 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium">
      <Icon name={mediaKindIcon(kind)} size="xs" color="inherit" aria-hidden />
      {t(mediaKindLabelKey(kind))}
    </span>
  );
}

/**
 * Weighted criteria meter: each scored criterion is a segment sized by its DS weight and
 * tinted by result. Hovering a segment reveals its detail (result, weight, note) in a fixed
 * readout below — no floating tooltip that chases the cursor across the dense segments;
 * clicking one jumps to its row in the breakdown. The breakdown table below is the accessible
 * source of truth. Segments animate in on mount.
 */
function CriteriaSummary({
  asset,
  activeKey,
  onHover,
  onSelect,
}: {
  asset: PreflightReportAsset;
  activeKey: string | null;
  onHover: (key: string | null) => void;
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const counts = {
    success: asset.features.filter((f) => f.state === "PASS").length,
    error: asset.features.filter((f) => f.state === "FAIL").length,
    warning: asset.features.filter((f) => f.state === "PARTIAL").length,
  };

  const weighted = asset.features.filter((f) => f.weight > 0);
  const totalWeight = weighted.reduce((sum, f) => sum + f.weight, 0);
  const segments = [...weighted].sort(
    (a, b) =>
      TONE_ORDER.indexOf(RESULT_STATE_META[a.state].tone) -
      TONE_ORDER.indexOf(RESULT_STATE_META[b.state].tone),
  );

  const legend = (
    [
      {
        tone: "success",
        count: counts.success,
        labelKey: "preflight.creativeCard.passedCount",
      },
      {
        tone: "error",
        count: counts.error,
        labelKey: "preflight.creativeCard.failedCount",
      },
      {
        tone: "warning",
        count: counts.warning,
        labelKey: "preflight.creativeCard.partialCount",
      },
    ] as const
  ).filter((entry) => entry.count > 0);

  const active = segments.find((f) => (f.code ?? f.name) === activeKey) ?? null;
  const activeTone = active ? RESULT_STATE_META[active.state].tone : null;

  return (
    <div className="flex flex-col gap-2">
      {totalWeight > 0 ? (
        // Visual score-composition meter; the breakdown table below is the accessible source of
        // truth, so segment hover/click stay mouse-only enhancements of that table.
        <div
          className="bg-muted flex h-3 w-full gap-px overflow-hidden rounded-full"
          aria-hidden
          onMouseLeave={() => onHover(null)}
        >
          {segments.map((feature, index) => {
            const tone = RESULT_STATE_META[feature.state].tone;
            const share = (feature.weight / totalWeight) * 100;
            const key = feature.code ?? feature.name;
            const dimmed = activeKey != null && activeKey !== key;
            return (
              <span
                key={feature.code ?? `${feature.name}-${index}`}
                className={cn(
                  "h-full min-w-0 cursor-pointer transition-[width,opacity] duration-300 ease-out first:rounded-l-full last:rounded-r-full motion-reduce:transition-none",
                  TONE_BG[tone],
                )}
                style={{
                  width: mounted ? `${share}%` : "0%",
                  opacity: mounted ? (dimmed ? 0.4 : 1) : 0,
                  transitionDelay: mounted ? "0ms" : `${index * 40}ms`,
                }}
                onMouseEnter={() => onHover(key)}
                onClick={(event) => {
                  // The card header toggles the breakdown on click; a segment always opens it.
                  event.stopPropagation();
                  onSelect(key);
                }}
              />
            );
          })}
        </div>
      ) : null}

      <div className="flex min-h-4 items-center text-xs">
        {active && activeTone ? (
          <span className="flex w-full min-w-0 items-center gap-1.5 whitespace-nowrap">
            <span
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                TONE_BG[activeTone],
              )}
              aria-hidden
            />
            <span className="text-foreground shrink-0 font-medium">
              {active.name}
            </span>
            <span
              className={cn("shrink-0 tabular-nums", TONE_TEXT[activeTone])}
            >
              {featureResultLabel(active, t)}
            </span>
            <span className="text-muted-foreground shrink-0 tabular-nums">
              · {weightPercent(active.weight)}
            </span>
            <span className="text-muted-foreground min-w-0 truncate">
              · {featureNote(active, t)}
            </span>
          </span>
        ) : (
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
            {legend.map((entry) => (
              <span
                key={entry.tone}
                className={cn(
                  "inline-flex items-center gap-1.5",
                  TONE_TEXT[entry.tone],
                )}
              >
                <span
                  className={cn("size-1.5 rounded-full", TONE_BG[entry.tone])}
                  aria-hidden
                />
                {t(entry.labelKey, { count: entry.count })}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export interface CreativeResultCardProps {
  asset: PreflightReportAsset;
  /**
   * 1-based position in the report, used to name creatives that were ingested by URL and so
   * have no filename. Fixed to the report order, not the current sort/filter.
   */
  position?: number;
  /**
   * Parent report — enables the per-asset export menu (Export item 2). Omitted in
   * isolated contexts (Storybook, in-progress reports) where exporting makes no sense.
   */
  report?: PreflightReportDetail;
  /** Gate the per-asset export affordance on the report being in a terminal, exportable state. */
  exportable?: boolean;
  /** The report is still analyzing this asset — show an in-progress treatment (report-ui R12). */
  inProgress?: boolean;
  /** Report-level retry re-runs all failed assets; shown only on failed cards when allowed. */
  showRetry?: boolean;
  retryPending?: boolean;
  onRetry?: () => void;
  /**
   * Cross-view focus request (heatmap cell → row): expands the breakdown, scrolls the
   * matching criterion into view and flash-highlights it. A fresh object retriggers it.
   */
  focus?: { key: string } | null;
  /**
   * Jump to this card as a whole (score-rail dot → creative): outlines it while the page
   * scrolls, then expands the breakdown. A fresh object retriggers it.
   */
  reveal?: { assetId: string } | null;
  /**
   * Bulk expand/collapse broadcast from the list toolbar. A fresh object forces this card's
   * open state to `open`, while the per-card chevron still toggles independently afterwards.
   */
  collapseSignal?: { open: boolean } | null;
  className?: string;
}

/** Matches the breakdown's grid expand transition so we scroll only after the row is laid out. */
const OPEN_ANIMATION_MS = 300;
/** Small buffer on top of the expand transition so the row's final position is settled. */
const SCROLL_SETTLE_MS = 20;
/** How long the cross-view jump keeps a row highlighted before fading back. */
const FLASH_DURATION_MS = 1800;
/** Lets the smooth scroll land before the breakdown grows, so the two don't animate at once. */
const REVEAL_OPEN_DELAY_MS = 340;

/** One creative's result: preview + score grouped together, criteria breakdown below. */
export function CreativeResultCard({
  asset,
  position = 1,
  report,
  exportable = false,
  inProgress = false,
  showRetry = false,
  retryPending = false,
  onRetry,
  focus = null,
  reveal = null,
  collapseSignal = null,
  className,
}: CreativeResultCardProps) {
  const { t } = useTranslation();
  const pending = inProgress || asset.status === "PENDING";
  const failed = asset.status === "FAILED";
  const scored = asset.status === "SCORED" && asset.score != null;
  const label = creativeDisplayLabel(
    asset.originalName,
    position,
    t("preflight.creativeCard.fallbackName"),
  );
  const showAssetExport = exportable && report != null && !pending && !failed;

  // Shared hover so the meter segment and its breakdown row highlight together (keyed by criterion).
  const [hoveredCriterion, setHoveredCriterion] = useState<string | null>(null);
  const [flashCriterion, setFlashCriterion] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const bodyId = useId();

  const highlightTimers = useRef<number[]>([]);

  /** Opens the breakdown, scrolls a criterion's row into view and flash-highlights it. */
  const highlightCriterion = useCallback(
    (key: string) => {
      highlightTimers.current.forEach(window.clearTimeout);
      setOpen(true);
      setFlashCriterion(key);
      const rowId = criterionRowId(bodyId, key);
      highlightTimers.current = [
        window.setTimeout(() => {
          document
            .getElementById(rowId)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, OPEN_ANIMATION_MS + SCROLL_SETTLE_MS),
        window.setTimeout(() => {
          setFlashCriterion((current) => (current === key ? null : current));
        }, FLASH_DURATION_MS),
      ];
    },
    [bodyId],
  );

  useEffect(
    () => () => highlightTimers.current.forEach(window.clearTimeout),
    [],
  );

  // Heatmap-cell jump: open the breakdown, scroll the criterion into view, briefly highlight it.
  useEffect(() => {
    if (!focus) return;
    highlightCriterion(focus.key);
  }, [focus, highlightCriterion]);

  // Score-rail jump: outline the card straight away so the eye can follow it in while the page
  // scrolls, then expand the breakdown once that scroll has settled.
  useEffect(() => {
    if (!reveal) return;
    setRevealed(true);
    const openTimer = window.setTimeout(() => {
      if (scored) setOpen(true);
    }, REVEAL_OPEN_DELAY_MS);
    const clearTimer = window.setTimeout(
      () => setRevealed(false),
      REVEAL_OPEN_DELAY_MS + FLASH_DURATION_MS,
    );
    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(clearTimer);
    };
  }, [reveal, scored]);

  // Bulk expand/collapse from the list toolbar.
  useEffect(() => {
    if (!collapseSignal) return;
    setOpen(collapseSignal.open);
  }, [collapseSignal]);

  const activeCriterion = hoveredCriterion ?? flashCriterion;

  // Clicking anywhere on the header toggles the breakdown — except on nested controls
  // (preview lightbox, export menu, the chevron itself), which keep their own behaviour.
  const handleHeaderClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!scored) return;
    if ((event.target as HTMLElement).closest("button, a")) return;
    setOpen((prev) => !prev);
  };

  return (
    <Card
      className={cn(
        "gap-0 overflow-hidden border-border/60 py-0",
        "transition-[box-shadow,border-color] duration-500 ease-out motion-reduce:transition-none",
        revealed && "border-primary/70 ring-primary/15 ring-[3px]",
        className,
      )}
    >
      <div
        onClick={handleHeaderClick}
        className={cn(
          "flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-4",
          (!scored || open) && "border-b border-border/60",
          scored && "cursor-pointer",
        )}
      >
        <div className="w-full shrink-0 sm:w-36">
          <ReportAssetPreview asset={asset} label={label} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="min-w-0">
              <AssetNameLabel
                name={label}
                detail={asset.sourceUrl}
                className="text-foreground text-sm font-medium"
              />
            </h3>
            <MediaTypeChip contentType={asset.contentType} />
            {asset.sourceUrl ? <SourceLink url={asset.sourceUrl} /> : null}
          </div>
          {pending ? (
            <StatusBadge status="ANALYZING" />
          ) : scored && asset.features.length > 0 ? (
            <CriteriaSummary
              asset={asset}
              activeKey={activeCriterion}
              onHover={setHoveredCriterion}
              onSelect={highlightCriterion}
            />
          ) : null}
        </div>

        {scored || showAssetExport ? (
          <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
            {scored ? (
              <ScoreBadge
                score={asset.score}
                band={asset.band}
                variant="card"
                className="sm:items-end sm:text-right"
              />
            ) : null}
            <div className="text-muted-foreground flex items-center gap-0.5 self-stretch border-border/60 sm:self-center sm:border-l sm:pl-3">
              {showAssetExport ? (
                <>
                  <AssetCopyTextButton report={report} asset={asset} />
                  <AssetExportMenu report={report} asset={asset} />
                </>
              ) : null}
              {scored ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-expanded={open}
                  aria-controls={bodyId}
                  aria-label={t(
                    open
                      ? "preflight.creativeCard.collapse"
                      : "preflight.creativeCard.expand",
                  )}
                  onClick={() => setOpen((prev) => !prev)}
                  className="text-muted-foreground shrink-0"
                >
                  <Icon
                    name="chevron-down"
                    size="sm"
                    color="inherit"
                    className={cn(
                      "transition-transform duration-200 motion-reduce:transition-none",
                      open ? "" : "-rotate-90",
                    )}
                  />
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {pending ? (
        <div className="flex flex-col gap-2 p-5 pt-4" aria-live="polite">
          <Skeleton className="h-4 w-full rounded" />
          <Skeleton className="h-4 w-5/6 rounded" />
          <p className="text-muted-foreground text-xs">
            {t("preflight.creativeCard.analyzing")}
          </p>
        </div>
      ) : failed ? (
        <div className="p-5 pt-4">
          <div
            className="bg-error-subtle/25 border-error/25 flex gap-2.5 rounded-md border px-3 py-3"
            role="alert"
          >
            <Icon
              name="alert-circle"
              size="sm"
              className="text-error mt-0.5 shrink-0"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-sm font-medium">
                {t("preflight.creativeCard.failedTitle")}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {formatAssetError(asset.error, t)}
              </p>
              {showRetry && onRetry ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  iconStart="refresh-cw"
                  disabled={retryPending}
                  onClick={onRetry}
                >
                  {t("preflight.creativeCard.retry")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div
          id={bodyId}
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className="p-5 pt-4">
              <CriteriaBreakdownTable
                features={asset.features}
                activeKey={activeCriterion}
                onHover={setHoveredCriterion}
                rowIdPrefix={bodyId}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
